import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Shield, CheckCircle, XCircle, Plus, Clock, Building2, LogOut, Users, Trash2, Upload, Image } from 'lucide-react';
import { cn } from '../lib/utils';

export default function SuperadminPage() {
  const [token, setToken] = useState(() => localStorage.getItem('sa_token') || '');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  const [requests, setRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({ login_logo: null });
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(false);
  const [createDlg, setCreateDlg] = useState(false);
  const [createForm, setCreateForm] = useState({ company_name: '', admin_email: '', admin_password: '', admin_name: '' });
  const [creating, setCreating] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const saFetch = async (method, url, data) => {
    const res = await fetch(`${BACKEND_URL}/api${url}`, {
      method: method.toUpperCase(),
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: data ? JSON.stringify(data) : undefined
    });
    return res.json();
  };

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [reqData, userData, tenantData, settingsData] = await Promise.all([
        saFetch('GET', '/superadmin/requests'),
        saFetch('GET', '/superadmin/users'),
        saFetch('GET', '/superadmin/tenants'),
        saFetch('GET', '/superadmin/global-settings')
      ]);
      if (reqData.detail && reqData.detail.includes('Superadmin')) {
        toast.error('Sitzung abgelaufen'); setToken(''); localStorage.removeItem('sa_token'); return;
      }
      if (Array.isArray(reqData)) setRequests(reqData);
      if (Array.isArray(userData)) setAllUsers(userData);
      if (Array.isArray(tenantData)) setAllTenants(tenantData);
      if (settingsData && !settingsData.detail) setGlobalSettings(settingsData);
    } catch { toast.error('Fehler beim Laden'); } finally { setLoading(false); }
  };

  useEffect(() => { if (token) loadData(); }, [token]);

  const handleDeleteTenant = async (tenantId, companyName) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie "${companyName}" und ALLE zugehörigen Daten (Benutzer, Aufträge, Verwaltungen, etc.) unwiderruflich löschen möchten?`)) return;
    setDeletingTenant(tenantId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/superadmin/tenants/${tenantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Tenant gelöscht');
        loadData();
      } else {
        toast.error(data.detail || 'Fehler beim Löschen');
      }
    } catch { toast.error('Verbindungsfehler'); }
    finally { setDeletingTenant(null); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilder erlaubt');
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error('Logo darf maximal 1 MB sein');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/superadmin/global-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Login-Logo aktualisiert');
        setGlobalSettings(prev => ({ ...prev, login_logo: data.login_logo }));
      } else {
        toast.error(data.detail || 'Upload fehlgeschlagen');
      }
    } catch { toast.error('Verbindungsfehler'); }
    finally { setUploadingLogo(false); if (logoInputRef.current) logoInputRef.current.value = ''; }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Login-Logo entfernen?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/superadmin/global-logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Logo entfernt');
        setGlobalSettings(prev => ({ ...prev, login_logo: null }));
      }
    } catch { toast.error('Fehler'); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/superadmin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('sa_token', data.access_token);
        setToken(data.access_token);
        toast.success('Angemeldet');
      } else { toast.error(data.detail || 'Falsche Zugangsdaten'); }
    } catch { toast.error('Verbindungsfehler'); } finally { setLoggingIn(false); }
  };

  const handleApprove = async (id) => {
    const data = await saFetch('POST', `/superadmin/approve/${id}`);
    if (data.tenant_slug) {
      toast.success(`Genehmigt! Unternehmens-ID: ${data.tenant_slug}`);
      loadData();
    } else toast.error(data.detail || 'Fehler beim Genehmigen');
  };

  const handleReject = async (id) => {
    if (!window.confirm('Anfrage wirklich ablehnen?')) return;
    const data = await saFetch('POST', `/superadmin/reject/${id}`);
    if (data.message) { toast.success('Abgelehnt'); loadData(); }
    else toast.error(data.detail || 'Fehler');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.company_name || !createForm.admin_email || !createForm.admin_password || !createForm.admin_name) {
      toast.error('Alle Felder sind pflicht'); return;
    }
    setCreating(true);
    try {
      const data = await saFetch('POST', '/superadmin/create-tenant', createForm);
      if (data.tenant_slug) {
        toast.success(`Tenant erstellt! Unternehmens-ID: ${data.tenant_slug}`);
        setCreateDlg(false);
        setCreateForm({ company_name: '', admin_email: '', admin_password: '', admin_name: '' });
        loadData();
      } else toast.error(data.detail || 'Fehler beim Erstellen');
    } catch { toast.error('Verbindungsfehler'); } finally { setCreating(false); }
  };

  const handlePasswordReset = async (userId, email) => {
    if (!window.confirm(`Passwort-Reset-Link an ${email} senden?`)) return;
    const data = await saFetch('POST', `/superadmin/reset-password/${userId}`);
    if (data.message) toast.success(data.message);
    else toast.error(data.detail || 'Fehler');
  };

  const logout = () => { localStorage.removeItem('sa_token'); setToken(''); setRequests([]); setAllUsers([]); };

  const pending = requests.filter(r => r.status === 'pending');
  const processed = requests.filter(r => r.status !== 'pending');

  // Login screen
  if (!token) return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center"><Shield className="w-4 h-4 text-primary-foreground" /></div>
          <span className="font-heading font-black text-xl tracking-tight">Your Hero – Superadmin</span>
        </div>
        <h2 className="font-heading font-bold text-2xl mb-6">Superadmin Login</h2>
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" autoFocus /></div>
          <div className="space-y-1.5"><Label>Passwort</Label><Input type="password" value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} className="rounded-sm" /></div>
          <Button type="submit" className="w-full rounded-sm" disabled={loggingIn}>{loggingIn ? 'Anmelden...' : 'Anmelden'}</Button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center"><Shield className="w-4 h-4 text-primary-foreground" /></div>
          <div><h1 className="font-heading font-black text-2xl">Superadmin Panel</h1><p className="text-xs text-muted-foreground">Your Hero Verwaltung</p></div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDlg(true)} size="sm" className="rounded-sm gap-2"><Plus className="w-4 h-4" />Neuer Tenant</Button>
          <Button variant="outline" onClick={logout} size="sm" className="rounded-sm gap-2"><LogOut className="w-4 h-4" />Abmelden</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-muted p-1 rounded-sm w-fit">
        {[['requests', 'Anfragen'], ['tenants', 'Alle Tenants'], ['users', 'Benutzer'], ['settings', 'Einstellungen']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={cn('px-4 py-1.5 text-sm rounded-sm transition-colors', activeTab === key ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground')}>
            {label}{key === 'requests' && pending.length > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-sm">{pending.length}</span>}
            {key === 'tenants' && <span className="ml-1.5 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-sm">{allTenants.length}</span>}
          </button>
        ))}
      </div>

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-amber-600" />
              <h2 className="font-heading font-semibold text-lg">Ausstehende Anfragen</h2>
              {pending.length > 0 && <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-sm">{pending.length}</span>}
            </div>
            {loading ? (
              <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : pending.length === 0 ? (
              <div className="bg-card border border-border/60 rounded-sm p-6 text-center text-sm text-muted-foreground">Keine ausstehenden Anfragen</div>
            ) : (
              <div className="space-y-3">
                {pending.map(r => (
                  <div key={r.id} className="bg-card border border-amber-200 rounded-sm p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <h3 className="font-semibold">{r.company_name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{r.admin_name} · {r.admin_email}</p>
                        {r.phone && <p className="text-xs text-muted-foreground">{r.phone}</p>}
                        {r.notes && <p className="text-xs text-muted-foreground italic mt-1">{r.notes}</p>}
                        <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString('de-CH')}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button onClick={() => handleApprove(r.id)} size="sm" className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle className="w-3.5 h-3.5" />Genehmigen
                        </Button>
                        <Button variant="outline" onClick={() => handleReject(r.id)} size="sm" className="rounded-sm gap-1 border-red-200 text-red-600 hover:bg-red-50">
                          <XCircle className="w-3.5 h-3.5" />Ablehnen
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {processed.length > 0 && (
            <div>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Bearbeitete Anfragen</h2>
              <div className="space-y-2">
                {processed.map(r => (
                  <div key={r.id} className="bg-card border border-border/60 rounded-sm px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{r.company_name}</p>
                      <p className="text-xs text-muted-foreground">{r.admin_email}</p>
                      {r.tenant_slug && <p className="text-xs font-mono text-primary">ID: {r.tenant_slug}</p>}
                    </div>
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-sm', r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>
                      {r.status === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-semibold text-lg">Alle Benutzer ({allUsers.length})</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="space-y-2">
              {allUsers.map(u => (
                <div key={u.id} className="bg-card border border-border/60 rounded-sm px-4 py-3 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{u.name || u.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email} · {u.company_name}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={cn('text-xs px-2 py-0.5 rounded-sm', u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground')}>{u.role}</span>
                    <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => handlePasswordReset(u.id, u.email)}>Reset-Link senden</Button>
                  </div>
                </div>
              ))}
              {allUsers.length === 0 && <div className="text-center py-8 text-sm text-muted-foreground">Keine Benutzer</div>}
            </div>
          )}
        </div>
      )}

      {/* All Tenants Tab */}
      {activeTab === 'tenants' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-blue-600" />
            <h2 className="font-heading font-semibold text-lg">Alle Tenants ({allTenants.length})</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="space-y-2">
              {allTenants.map(t => {
                const tenantUsers = allUsers.filter(u => u.tenant_id === t.id);
                return (
                  <div key={t.id} className="bg-card border border-border/60 rounded-sm p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-blue-600" />
                          <h3 className="font-semibold">{t.company_name}</h3>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Unternehmens-ID: <code className="bg-muted px-1 py-0.5 rounded">{t.slug}</code>
                        </p>
                        {t.company_address && <p className="text-xs text-muted-foreground mt-1">{t.company_address}</p>}
                        <p className="text-xs text-muted-foreground mt-1">
                          Erstellt: {new Date(t.created_at).toLocaleDateString('de-CH')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-2">
                          {tenantUsers.length} Benutzer
                        </p>
                        <div className="flex flex-col gap-1">
                          {tenantUsers.slice(0, 2).map(u => (
                            <p key={u.id} className="text-xs">
                              {u.email} <span className={cn('px-1 py-0.5 rounded-sm text-[10px]', u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-muted')}>{u.role}</span>
                            </p>
                          ))}
                          {tenantUsers.length > 2 && <p className="text-xs text-muted-foreground">+{tenantUsers.length - 2} weitere</p>}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-3 rounded-sm text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                          onClick={() => handleDeleteTenant(t.id, t.company_name)}
                          disabled={deletingTenant === t.id}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          {deletingTenant === t.id ? 'Löschen...' : 'Löschen'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {allTenants.length === 0 && <div className="text-center py-8 text-sm text-muted-foreground">Keine Tenants vorhanden</div>}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Image className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-semibold text-lg">Globale Einstellungen</h2>
          </div>
          
          <div className="bg-card border border-border/60 rounded-sm p-6 space-y-6">
            {/* Login Logo */}
            <div>
              <h3 className="font-medium mb-3">Login-Seite Logo</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Dieses Logo wird auf der Login-Seite für alle Benutzer angezeigt.
              </p>
              
              <div className="flex items-start gap-6">
                <div className="w-32 h-16 bg-muted rounded-sm flex items-center justify-center overflow-hidden border border-border">
                  {globalSettings.login_logo ? (
                    <img src={globalSettings.login_logo} alt="Login Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-xs text-muted-foreground">Kein Logo</span>
                  )}
                </div>
                
                <div className="space-y-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="global-logo-upload"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm gap-2"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingLogo ? 'Hochladen...' : 'Logo hochladen'}
                    </Button>
                    {globalSettings.login_logo && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-destructive hover:text-destructive"
                        onClick={handleDeleteLogo}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Empfohlen: PNG oder SVG, max. 1 MB, Querformat
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Tenant Dialog */}
      <Dialog open={createDlg} onOpenChange={setCreateDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Neuen Tenant erstellen</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Unternehmensname *</Label><Input value={createForm.company_name} onChange={e => setCreateForm(f => ({ ...f, company_name: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Admin Name *</Label><Input value={createForm.admin_name} onChange={e => setCreateForm(f => ({ ...f, admin_name: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Admin E-Mail *</Label><Input type="email" value={createForm.admin_email} onChange={e => setCreateForm(f => ({ ...f, admin_email: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Admin Passwort *</Label><Input type="password" value={createForm.admin_password} onChange={e => setCreateForm(f => ({ ...f, admin_password: e.target.value }))} className="rounded-sm" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDlg(false)} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" disabled={creating} className="rounded-sm">{creating ? 'Erstellen...' : 'Tenant erstellen'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
